import type { Outcome } from "./types";

/**
 * Retro-grill (Agent Brains section 13.2).
 *
 * Verbatim: "Weekly batch grills outcomes... Loss post-mortems: 'Which
 * message preceded the ghost? What question was never asked? What did the
 * customer signal that got missed?' -> hypotheses for the queue. Win
 * post-mortems: 'Skill or luck?' - the anti-lucky-win check (B4) run as
 * explicit self-questioning; a win the agent can't explain doesn't become
 * doctrine."
 *
 * A BATCH job, not a per-message thing, over a window of CLOSED threads only
 * (won, lost, ghosted). Never reads or writes live customer-facing state.
 *
 * PATTERN: mirrors `buildOutcomeDigest` (`outcomeDigest.ts`), this repo's
 * closest existing batch-over-threads shape: a pure function taking
 * pre-loaded per-thread facts and producing a structured result. Same split
 * of labour as that module: a DB loader (mirroring `collectOutcomeDigest` /
 * `emitWeeklyDigest`) would narrow rows and hand them here; this decides.
 * That loader was not built (FINDINGS TA-179): this module is the pure half
 * only, so `pnpm test` exercises it without the heavier Postgres harness
 * `packages/db`'s equivalent integration tests require.
 *
 * WHY THIS DOES NOT GENERATE PROSE. Section 0's precision doctrine and the
 * "model never self-grades" rule in section 4 both argue against a pure,
 * unit-tested core module inventing a hypothesis or a verdict from raw text.
 * What IS deterministic, and what this module owns:
 *   - packaging the vault's own loss questions per thread, verbatim, so a
 *     downstream call (a frontier read over the closing turns, the way
 *     `buildPlaycard` builds a card for a downstream compose call) has a
 *     stable slot to answer into, and saying plainly when nothing answered it
 *     yet, rather than inventing a hypothesis to fill the slot;
 *   - B4's anti-lucky-win arithmetic on the win side: counting how many
 *     threads IN THIS BATCH share a caller-supplied candidate trait, and
 *     comparing that count to the evidence bar, which is exactly the kind of
 *     counting `buildOutcomeDigest` already does and an LLM must not do
 *     itself (section 4: math through deterministic code, never LLM
 *     arithmetic).
 */

/** Section 13.2's three loss questions, transcribed verbatim. */
export const LOSS_QUESTIONS: readonly string[] = [
  "Which message preceded the ghost?",
  "What question was never asked?",
  "What did the customer signal that got missed?",
];

/** Section 13.2's win question, transcribed verbatim. */
export const WIN_QUESTION = "Skill or luck?";

/**
 * B4's anti-lucky-win evidence bar. FINDINGS TA-179: this is F-15's
 * `PATTERN_MIN_SAMPLE` (`packages/agents/src/playcard.ts`), duplicated here
 * rather than imported because `packages/core` cannot depend on
 * `packages/agents` (the dependency runs the other way). The two must move
 * together if F-15's bar changes; Kabir's call, logged as an open item.
 * `packages/agents/src/constantsSync.test.ts` (TA-239) fails when they
 * disagree.
 */
export const RETRO_GRILL_WIN_MIN_SAMPLE = 5;

export type WinVerdict = "corroborated" | "isolated" | "insufficient_data";

export interface RetroGrillThread {
  readonly threadId: string;
  readonly contactId: string;
  readonly outcome: Outcome;
  readonly closedAt: Date;
  /**
   * Free-text hypothesis for what preceded the loss, already produced
   * upstream of this module (out of scope here: see the module doc). Null
   * when nothing was supplied, reported as such rather than guessed.
   */
  readonly lossHypothesis: string | null;
  /**
   * What this win would credit (a framework, a pattern id, a move), supplied
   * by the caller. Null when nothing is credited, which the B4 check reports
   * as `insufficient_data` rather than silently skipping.
   */
  readonly candidateTrait: string | null;
}

export interface RetroGrillLossFinding {
  readonly threadId: string;
  readonly contactId: string;
  readonly questions: readonly string[];
  readonly hypothesis: string | null;
  /** True only when a hypothesis was actually supplied. Nothing here invents one. */
  readonly hasHypothesis: boolean;
}

export interface RetroGrillWinFinding {
  readonly threadId: string;
  readonly contactId: string;
  readonly question: string;
  readonly candidateTrait: string | null;
  /** How many threads in THIS batch share the trait, `candidateTrait` itself included. */
  readonly corroboratingThreads: number;
  readonly verdict: WinVerdict;
}

export interface RetroGrillBatch {
  readonly periodStart: Date;
  readonly periodEnd: Date;
  /** Losses plus wins. Does not include `skipped`. */
  readonly threadsGrilled: number;
  readonly losses: readonly RetroGrillLossFinding[];
  readonly wins: readonly RetroGrillWinFinding[];
  /** Threads this batch does not grill (`ongoing`: nothing has closed yet to post-mortem). */
  readonly skipped: number;
}

const LOSS_OUTCOMES = new Set<Outcome>(["lost", "ghosted"]);
const WIN_OUTCOMES = new Set<Outcome>(["sold", "booked", "deposit"]);

export function buildRetroGrillBatch(input: {
  readonly threads: readonly RetroGrillThread[];
  readonly periodStart: Date;
  readonly periodEnd: Date;
}): RetroGrillBatch {
  // B4's bar is counted first: a single thread cannot judge its own
  // corroboration, and every win in the batch needs the finished tally before
  // any one of them can be verdicted.
  const traitCounts = new Map<string, number>();
  for (const t of input.threads) {
    if (!WIN_OUTCOMES.has(t.outcome) || t.candidateTrait === null) continue;
    traitCounts.set(t.candidateTrait, (traitCounts.get(t.candidateTrait) ?? 0) + 1);
  }

  const losses: RetroGrillLossFinding[] = [];
  const wins: RetroGrillWinFinding[] = [];
  let skipped = 0;

  for (const t of input.threads) {
    if (LOSS_OUTCOMES.has(t.outcome)) {
      losses.push({
        threadId: t.threadId,
        contactId: t.contactId,
        questions: LOSS_QUESTIONS,
        hypothesis: t.lossHypothesis,
        hasHypothesis: t.lossHypothesis !== null,
      });
    } else if (WIN_OUTCOMES.has(t.outcome)) {
      const trait = t.candidateTrait;
      const corroboratingThreads = trait === null ? 0 : (traitCounts.get(trait) ?? 0);
      const verdict: WinVerdict =
        trait === null
          ? "insufficient_data"
          : corroboratingThreads >= RETRO_GRILL_WIN_MIN_SAMPLE
            ? "corroborated"
            : "isolated";
      wins.push({
        threadId: t.threadId,
        contactId: t.contactId,
        question: WIN_QUESTION,
        candidateTrait: trait,
        corroboratingThreads,
        verdict,
      });
    } else {
      // `ongoing`: nothing has closed, so there is nothing to post-mortem yet.
      skipped += 1;
    }
  }

  return {
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    threadsGrilled: losses.length + wins.length,
    losses,
    wins,
    skipped,
  };
}
